import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { IntakeSectionsView } from "@/components/admin/IntakeSectionsView";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/formularios/$id")({
  component: FormularioDetalle,
});

function FormularioDetalle() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState<any>(null);
  const [order, setOrder] = useState<any>(null);
  const [customer, setCustomer] = useState<any>(null);
  const [showJson, setShowJson] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: f } = await supabase.from("intake_forms").select("*").eq("id", id).maybeSingle();
      setForm(f);
      if (f?.order_id) {
        const { data: o } = await supabase
          .from("orders")
          .select("id,status,total,created_at,paid_at,customer_id,stripe_session_id")
          .eq("id", f.order_id)
          .maybeSingle();
        setOrder(o);
        if (o?.customer_id) {
          const { data: c } = await supabase
            .from("customers")
            .select("id,full_name,email,phone,company_name")
            .eq("id", o.customer_id)
            .maybeSingle();
          setCustomer(c);
        }
      }
      setLoading(false);
    })();
  }, [id]);

  // Resolver customerId desde múltiples fuentes
  const resolvedCustomerId: string | null = customer?.id ?? order?.customer_id ?? null;

  function goToOrder() {
    if (!order?.id) { toast.error("Este formulario no tiene pedido asociado"); return; }
    navigate({ to: "/admin/pedidos/$id", params: { id: order.id } });
  }
  function goToCustomer() {
    if (!resolvedCustomerId) { toast.error("Este formulario no tiene cliente asociado"); return; }
    navigate({ to: "/admin/clientes/$id", params: { id: resolvedCustomerId } });
  }

  if (loading) return <div className="text-muted-foreground">Cargando…</div>;
  if (!form) {
    return (
      <div className="space-y-3">
        <Link to="/admin/intakes" className="text-xs text-muted-foreground hover:text-foreground">← Volver a formularios</Link>
        <div className="rounded-md border border-hairline bg-surface p-6 text-sm text-muted-foreground">Formulario no encontrado.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/admin/intakes" className="text-xs text-muted-foreground hover:text-foreground">← Volver a formularios</Link>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={goToOrder}
            disabled={!order?.id}
            className="cursor-pointer rounded-md border border-hairline bg-surface px-3 py-1.5 text-xs text-foreground hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Ver pedido →
          </button>
          <button
            type="button"
            onClick={goToCustomer}
            disabled={!resolvedCustomerId}
            className="cursor-pointer rounded-md border border-hairline bg-surface px-3 py-1.5 text-xs text-foreground hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Ver cliente →
          </button>
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-semibold text-foreground">Formulario de datos del pedido</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {form.legal_full_name ?? "—"} · {form.email ?? "—"} · {form.phone ?? "—"}
        </p>
        <p className="text-xs text-muted-foreground">Enviado el {new Date(form.submitted_at).toLocaleString()}</p>
      </div>

      {!resolvedCustomerId && (
        <div className="rounded-md border border-yellow-500/30 bg-yellow-500/10 p-3 text-xs text-yellow-700 dark:text-yellow-400">
          ⚠️ Este formulario no tiene cliente asociado. Verifica el pedido vinculado.
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {order && (
          <div className="rounded-md border border-hairline bg-surface p-3 text-xs">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pedido asociado</div>
            <div className="mt-1 text-sm text-foreground">#{order.id.slice(0, 8)} · {order.status}</div>
            <button
              type="button"
              onClick={goToOrder}
              className="cursor-pointer text-primary hover:underline"
            >
              Abrir pedido →
            </button>
          </div>
        )}
        {customer && (
          <div className="rounded-md border border-hairline bg-surface p-3 text-xs">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Cliente asociado</div>
            <div className="mt-1 text-sm text-foreground">{customer.full_name} · {customer.email}</div>
            <button
              type="button"
              onClick={goToCustomer}
              className="cursor-pointer text-primary hover:underline"
            >
              Abrir cliente →
            </button>
          </div>
        )}
      </div>

      {form.sections ? (
        <IntakeSectionsView sections={form.sections} />
      ) : (
        <pre className="max-h-96 overflow-auto rounded bg-surface-2 p-3 text-xs text-muted-foreground">
          {JSON.stringify(form.payload, null, 2)}
        </pre>
      )}

      <div className="rounded-md border border-hairline bg-surface">
        <button
          type="button"
          onClick={() => setShowJson((v) => !v)}
          className="w-full px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          {showJson ? "▾" : "▸"} Ver JSON técnico
        </button>
        {showJson && (
          <pre className="max-h-96 overflow-auto border-t border-hairline bg-surface-2 p-3 text-xs text-muted-foreground">
            {JSON.stringify({ sections: form.sections, payload: form.payload }, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
